import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useNavigate, useParams } from "react-router-dom";
import { generateTokenAuthHeader } from "../../commonfunctions/authentication";
import { log } from "../../commonfunctions/logger";
import Badge from "../../components/Badge/Badge";
import OrderForm from "../../components/OrderForm/OrderForm";
import { Container } from "../../components/styled/styled-components";
import Icon from "../../components/Icon/icon";
import { IContext, Context } from "../../context/ContextApp";
import { useDocumentTitle } from "../../hooks/document-title";
import Alert from "../../components/Alert/Alert";
import { ProductDetailResponse } from "../../interfaces/interfaces";
import { Brand } from "../../interfaces/types";
import ProductLink from "./ProductLink/ProductLink";
import { AlertType, SalesStatus } from "../../interfaces/enums";
import { subdomainTypes } from "../../context/availableSubdomains";
import ProductDetailLoading from "./Skeleton";
import { transformBrandToUserReadable } from "../../commonfunctions/text-transform";

// image_types the webstore does not render on the product details page (e.g. 360 spin sets)
const IGNORED_IMAGE_TYPES = ["360"];

const isRenderableImage = (image: { image_type?: string }) =>
  !IGNORED_IMAGE_TYPES.includes(image.image_type ?? "");

const stripIgnoredImages = (product: ProductDetailResponse): ProductDetailResponse => ({
  ...product,
  images: (product.images ?? []).filter(isRenderableImage),
  colors: (product.colors ?? []).map((color) => ({
    ...color,
    images: (color.images ?? []).filter(isRenderableImage),
  })),
});

const ProductDetail = () => {
  const [product, setProduct] = useState<ProductDetailResponse | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const params = useParams<{ slug: string; brand: Brand }>();
  const location = useLocation();
  const { appBranch }: IContext = useContext(Context);
  useDocumentTitle(product ? `${product.code} ${product.name}` : params.slug || "");

  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (params.slug?.includes("-promo")) {
      return navigate("/");
    }

    const fetchProductDetails = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_HONEYBEE_ROOT_API}/products/${params.brand}/${appBranch}/${params.slug}`,
          generateTokenAuthHeader(),
        );

        if (res.ok) {
          const data = await res.json();
          const product = stripIgnoredImages(data);
          if (product.code && product.brand) {
            setProduct(product);
          }
        } else {
          const error = await res.text();
          throw error;
        }
      } catch (error) {
        log(error, "Failed to fetch product details");
      }
    };

    fetchProductDetails();
  }, [navigate, params.brand, params.slug, appBranch]);

  if (product) {
    const isAlertActive = ["ZT460B", "ZT461B", "ZT462B"].includes(product.code);

    return (
      <Container row className="product-details">
        <div className="left-content">
          {product.images.length === 0 ? (
            product.colors[0].images.length > 0 ? (
              <img
                style={{ width: "100%" }}
                src={product.colors[0].images[0].https_attachment_url_product}
                alt="color_image"
              />
            ) : (
              <img
                style={{ width: "100%" }}
                src="https://cdn.fashionbiz.com/Image+Not+Found.jpg"
                alt="image_not_found"
              />
            )
          ) : (
            <img
              style={{ width: "100%" }}
              src={product.images[0].https_attachment_url_product}
              alt="product_image"
            />
          )}
          <div className="product-information">
            {getBrandLogo(product.brand)}
            {appBranch && renderBadges(appBranch, product)}
            <p className="product-information__name">{product.name}</p>
            <p className="product-information__code">{product.code}</p>
          </div>
          <ProductLink
            stylesheetLink={product.stylesheet}
            slug={product.slug}
            name={product.name}
            brand={product.brand}
            description={product.description}
          />
        </div>
        <div className="right-content">
          {product.description && (
            <div className="product-description">
              <button
                type="button"
                className="product-description__toggle product-details__spread"
                aria-expanded={isDetailsOpen}
                aria-controls="product-description-panel"
                onClick={() => setIsDetailsOpen(!isDetailsOpen)}
              >
                <span>{isDetailsOpen ? "Hide Details" : "Show Details"}</span>
                <Icon name={isDetailsOpen ? "ArrowUp" : "ArrowDown"} iconWidth="1.1rem" />
              </button>
              <div
                id="product-description-panel"
                className={
                  isDetailsOpen
                    ? "product-description__panel product-description__panel--open"
                    : "product-description__panel"
                }
              >
                <div className="product-description__panel__inner">
                  {renderDescriptionSection("Sizes", product.description.sizes)}
                  {renderDescriptionSection("Fabric", product.description.fabric)}
                  {renderDescriptionSection("Features", product.description.features)}
                  {renderDescriptionSection("Cares", product.description.cares)}
                </div>
              </div>
            </div>
          )}
          {isAlertActive && (
            <Alert
              content={{
                type: AlertType.Warning,
                message:
                  "Some garments may experience slight dye migration/smudging on upper back; fades out after washing.",
              }}
            />
          )}
          <OrderForm product={product} animateCTA />
        </div>
      </Container>
    );
  } else {
    return <ProductDetailLoading />;
  }
};

// If we need custom banners for certain countries, we need to include "appBranch: subdomainTypes | ''" in the arguments for this function
const renderBadges = (appBranch: subdomainTypes, product: ProductDetailResponse) => {
  const sales_statuses = [SalesStatus.Clearance, SalesStatus.Discontinued];

  if (
    product.slug.includes(SalesStatus.Clearance) ||
    sales_statuses.includes(product.sales_status)
  ) {
    return (
      <div className="product-badges">
        <Badge>{product.sales_status}</Badge>
        <Badge>NON-RETURNABLE</Badge>
      </div>
    );
  }

  if (product.badge_info) {
    return (
      <div className="product-badges">
        {product.sales_status !== SalesStatus.Normal && <Badge>{product.sales_status}</Badge>}
        <Badge>{product.badge_info}</Badge>
      </div>
    );
  }

  if (product.sales_status !== SalesStatus.Normal) {
    return (
      <div className="product-badges">
        <Badge>{product.sales_status}</Badge>
      </div>
    );
  }
};

function renderDescriptionSection(title: string, items?: string[] | string) {
  if (items && items.length > 0) {
    return (
      <section className="product-desc__section">
        <h6 className="product-desc__title">{title}</h6>
        <ul className="product-desc__list">
          {Array.isArray(items) ? (
            items.map((item, index) => <li key={index}>{item}</li>)
          ) : (
            <li>{items}</li>
          )}
        </ul>
      </section>
    );
  }
}

const getBrandLogo = (brand: Brand) => {
  switch (brand) {
    case "biz-collection":
      return (
        <img
          className="brand-logo"
          src="https://s3.ap-southeast-2.amazonaws.com/cdn.fashionbiz.com/webstore/brand-logos/collection.svg"
          alt="brand-logo"
        />
      );
    case "biz-corporates":
      return (
        <img
          className="brand-logo"
          src="https://s3.ap-southeast-2.amazonaws.com/cdn.fashionbiz.com/webstore/brand-logos/corporates.svg"
          alt="brand-logo"
        />
      );
    case "syzmik":
    case "syzmik-boots":
      return (
        <span className="brand-logo-crop--syzmik">
          <img
            className="brand-logo brand-logo--syzmik"
            src="https://cdn.fashionbiz.com/webstore/brand-logos/SYZMIK_LOGO_CMYK_(WITH WORKWEAR).svg"
            alt="brand-logo"
          />
        </span>
      );
    case "inaura":
      return (
        <img
          className="brand-logo"
          src="https://cdn.fashionbiz.com/webstore/brand-logos/inaura.svg"
          alt="brand-logo"
        />
      );
    case "good-mates":
      return (
        <img
          className="brand-logo"
          src="https://cdn.fashionbiz.com/webstore/brand-logos/GOODMATES-Logo-Primary-Black%5B98%5D.svg"
          alt="brand-logo"
        />
      );
    default:
      return <h1>{transformBrandToUserReadable(brand)}</h1>;
  }
};

export default ProductDetail;
